// Valutatore del sopralluogo per lavorare SENZA RETE: specchio del motore Python (safety/engine.py + rule_engine.py).
// Calcola quali domande restano, quali documenti servono e a che punto si e' (nessun rischio: quello resta sul server,
// che ricalcola tutto all'invio ed e' l'autorita'). Nessuna dipendenza: gira nel browser e sotto Node, dove un test
// lo confronta col motore Python su sopralluoghi casuali (tests/test_field_engine_equivalence.py).

export const SURVEY_SCOPE = "_";
const ANSWERED = new Set(["yes", "no", "answered"]);
const GAP = new Set(["unknown", "not_verifiable", "not_applicable"]);
const MODULE_DOC = new Set(["NOISE", "VIBRATION_HAV", "CARCINOGENIC_SILICA", "ASBESTOS"]);
const GENERIC_SPEC_DOC = "DOC-SPEC";
const EXPIRING_DAYS = 30;
const TASK_SPECIFIC = /questa lavorazione|viene (effettivamente )?eseguit|come viene eseguit|quale metodo|materiale prevalente|a quale altezza|di che materiale|che tipo di|si svolge sopra/i;

export const akey = (scope, q, eq) => `${scope}|${q}${eq ? `@${eq}` : ""}`;
const norm = (t) => String(t || "").toLowerCase().replace(/\s+/g, " ").replace(/[^a-z0-9àèéìòù ]/g, "").trim();
const stateOf = (a) => (a && a.state !== undefined && a.state !== null ? String(a.state) : "");
const pyRound = (x) => { const f = Math.floor(x); const d = x - f; return d === 0.5 ? (f % 2 === 0 ? f : f + 1) : Math.round(x); }; // round() di Python

function valueForCtx(a) {
  const st = stateOf(a);
  if (st === "yes") return "yes";
  if (st === "no") return "no";
  if (st === "answered") return a.value === undefined ? null : a.value;
  return null;
}

const eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const num = (x) => {
  if (x === null || x === undefined || typeof x === "boolean" || x === "") return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
};

function resolve(kb, v) {
  if (v && typeof v === "object" && !Array.isArray(v) && "param" in v) {
    const p = kb.parameters[v.param];
    if (!p) throw new Error(`parametro normativo sconosciuto: ${v.param}`);
    return p.value;
  }
  return v;
}

function check(c, ctx, kb) {
  const actual = ctx[c.field] === undefined ? null : ctx[c.field];
  const op = c.operator;
  const empty = actual === null || actual === "" || (Array.isArray(actual) && actual.length === 0);
  if (op === "EXISTS") return !empty;
  if (op === "NOT_EXISTS") return empty;
  const expected = resolve(kb, c.value);
  if (actual === null) return false;
  switch (op) {
    case "EQ": return eq(actual, expected);
    case "NE": return !eq(actual, expected);
    case "GT": case "GTE": case "LT": case "LTE": {
      const a = num(actual), b = num(expected);
      if (a === null || b === null) return false;
      return { GT: a > b, GTE: a >= b, LT: a < b, LTE: a <= b }[op];
    }
    case "IN": return Array.isArray(actual) ? actual.some((x) => expected.includes(x)) : expected.includes(actual);
    case "NOT_IN": return Array.isArray(actual) ? !actual.some((x) => expected.includes(x)) : !expected.includes(actual);
    case "CONTAINS":
      if (Array.isArray(actual)) return actual.some((x) => eq(x, expected));
      return typeof actual === "string" && actual.includes(String(expected));
    default: throw new Error(`operatore non ammesso: ${op}`);
  }
}

function matches(rule, ctx, kb) {
  if (!rule.conditions.length) return true;
  const res = rule.conditions.map((c) => check(c, ctx, kb));
  return rule.logic === "AND" ? res.every(Boolean) : res.some(Boolean);
}

function firedRules(kb, ctx) {
  const out = Object.values(kb.rules).filter((r) => matches(r, ctx, kb));
  out.sort((a, b) => (a.priority - b.priority) || (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
  return out;
}

const parseDay = (raw) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw || ""));
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return Number.isNaN(d.getTime()) || d.getUTCMonth() !== +m[2] - 1 ? null : d;
};
const dayIso = (d) => d.toISOString().slice(0, 10);
const asOf = (survey) => parseDay(survey.as_of) || parseDay(new Date().toISOString());
const daysBetween = (a, b) => Math.round((a.getTime() - b.getTime()) / 86400000);

function unitsOf(survey, task, eqCode) {
  const out = [], seen = new Set();
  const list = ((survey.equipment_units || {})[`${task}:${eqCode}`]) || [];
  for (const u of list) {
    const uid = String((u && u.id) || "").trim();
    if (!uid || seen.has(uid)) continue;
    seen.add(uid);
    const nc = parseDay(u.next_check);
    out.push({ id: uid, label: String(u.label || uid).slice(0, 100), next_check: nc ? dayIso(nc) : null });
  }
  return out;
}

export function evaluate(kb, survey) {
  const answers = survey.answers || {};
  const docsState = survey.documents || {};
  const taskCodes = (survey.task_codes || []).filter((t) => kb.tasks[t]);
  const today = asOf(survey);
  const activeKeys = [];
  const keyMeta = {};
  const hazards = new Set();
  const reqdocs = {};
  const specialists = {};
  const equipmentInstances = [];

  const scopeOf = (task, q) => (kb.questions[q].scope === "survey" ? SURVEY_SCOPE : task);
  const addKey = (task, q, equipment) => {
    const scope = scopeOf(task, q);
    const k = akey(scope, q, equipment);
    if (!keyMeta[k]) {
      keyMeta[k] = { task_code: scope === SURVEY_SCOPE ? null : task, question_code: q, equipment_code: equipment || null };
      activeKeys.push(k);
    }
    return k;
  };
  const addHazard = (t, h) => { hazards.add(`${t}\u0000${h}`); };
  const addDoc = (code, reason, subject) => {
    const k = subject ? `${code}@${subject}` : code;
    const d = reqdocs[k] || (reqdocs[k] = { key: k, code, subject: subject || null, reasons: [] });
    if (!d.reasons.includes(reason)) d.reasons.push(reason);
  };
  const addSpec = (m, reason) => { (specialists[m] = specialists[m] || new Set()).add(reason); };

  const smart = !!survey.smart;
  const sigCache = {};
  const sig = (k) => {
    if (!(k in sigCache)) {
      const m = keyMeta[k];
      const q = kb.questions[m.question_code];
      sigCache[k] = TASK_SPECIFIC.test(q.text) ? `own|${k}`
        : JSON.stringify([norm(q.text), q.answer_type, q.options.map((o) => o.value).sort(), m.equipment_code]);
    }
    return sigCache[k];
  };
  const effective = () => {
    if (!smart) return answers;
    const eff = { ...answers };
    for (const t0 of taskCodes) {
      const proc0 = kb.processes[kb.phases[kb.tasks[t0].phase_code].process_code];
      const ex = proc0.exec_question_code;
      if (ex) {
        const k0 = akey(scopeOf(t0, ex), ex);
        if (!(k0 in eff)) eff[k0] = { question_code: ex, task_code: t0, equipment_code: null, state: "yes", value: null, virtual: "selected" };
      }
    }
    const firstReal = {};
    for (const k of activeKeys) if (k in answers && !(sig(k) in firstReal)) firstReal[sig(k)] = k;
    for (const k of activeKeys) {
      if (!(k in eff)) {
        const src = firstReal[sig(k)];
        if (src) { const m = keyMeta[k]; eff[k] = { ...answers[src], task_code: m.task_code, equipment_code: m.equipment_code, virtual: "twin" }; }
      }
    }
    return eff;
  };
  const context = (t, effAns) => {
    const task = kb.tasks[t];
    const phase = kb.phases[task.phase_code];
    const ctx = { tasks: [...(survey.task_codes || [])], "task.code": t, "task.phase": task.phase_code, "task.process": phase ? phase.process_code : null };
    for (const key of activeKeys) {
      const scope = key.split("|", 1)[0];
      if (scope !== SURVEY_SCOPE && scope !== t) continue;
      const a = effAns[key];
      if (!a || a.equipment_code) continue;
      ctx[`answer.${a.question_code}`] = valueForCtx(a);
      ctx[`state.${a.question_code}`] = stateOf(a);
    }
    return ctx;
  };

  for (const t of taskCodes) {
    const task = kb.tasks[t];
    const phase = kb.phases[task.phase_code];
    const proc = kb.processes[phase.process_code];
    for (const q of [...proc.entry_question_codes, ...task.entry_question_codes]) addKey(t, q);
    for (const h of task.baseline_hazard_codes) addHazard(t, h);
    for (let i = 0; i < 40; i++) {
      const before = activeKeys.length;
      const ctx = context(t, effective());
      for (const rule of firedRules(kb, ctx)) for (const a of rule.actions) if (a.action === "ASK") addKey(t, a.target);
      if (activeKeys.length === before) break;
    }
    const effNow = effective();
    const ctx = context(t, effNow);
    for (const k of [...activeKeys]) {
      const meta = keyMeta[k];
      const q = kb.questions[meta.question_code];
      if (q.answer_type !== "equipment_multi" || meta.equipment_code) continue;
      if (meta.task_code !== t && meta.task_code !== null) continue;
      const a = effNow[k];
      if (!a || stateOf(a) !== "answered") continue;
      for (const code of a.value || []) {
        const e = kb.equipment[code];
        if (!e) continue;
        const units = unitsOf(survey, t, code);
        for (const u of (units.length ? units : [null])) {
          equipmentInstances.push({ task_code: t, equipment_code: code, unit_id: u ? u.id : null, label: u ? u.label : null, next_check: u ? u.next_check : null });
          for (const d of e.required_doc_codes) addDoc(d, `equipment:${code}`, `${t}:${code}${u ? `#${u.id}` : ""}`);
        }
        for (const h of e.hazard_codes) addHazard(t, h);
        for (const m of e.specialist_modules) addSpec(m, `equipment:${code}`);
        for (const dq of e.detail_question_codes) addKey(t, dq, code);
      }
    }
    for (const rule of firedRules(kb, ctx)) {
      for (const a of rule.actions) {
        if (a.action === "ACTIVATE_HAZARD") addHazard(t, a.target);
        else if (a.action === "REQUIRE_DOC") addDoc(a.target, `rule:${rule.code}`, ((a.parameters || {}).per || "survey") === "task" ? t : null);
        else if (a.action === "SPECIALIST") addSpec(a.target, `rule:${rule.code}`);
      }
    }
  }

  const effFinal = effective();
  const nextQuestions = [], openItems = [], missingEvidence = [];
  let required = 0, done = 0, merged = 0;
  const seen = new Set();
  for (const k of activeKeys) {
    const meta = keyMeta[k];
    const q = kb.questions[meta.question_code];
    const a = effFinal[k];
    if (smart) {
      if (a && a.virtual === "selected") continue;
      const g = sig(k);
      if (!g.startsWith("own|")) {
        if (seen.has(g)) { merged++; continue; }
        seen.add(g);
      }
    }
    if (q.required) required++;
    if (!a) {
      if (q.required) nextQuestions.push({ key: k, ...meta, text: q.text, help_text: q.help_text, answer_type: q.answer_type, options: q.options, evidence_requirements: q.evidence_requirements, critical: q.critical });
      continue;
    }
    const st = stateOf(a);
    if (q.required && ANSWERED.has(st)) done++;
    if (GAP.has(st)) {
      openItems.push({ key: k, question_code: q.code, text: q.text, severity: q.critical ? "critical" : "major", task_code: meta.task_code, equipment_code: meta.equipment_code });
      if (q.required) done++;
    }
    if (ANSWERED.has(st) && q.evidence_requirements.length && !((a.evidence_ids || []).length) && st !== "no") {
      missingEvidence.push({ key: k, question_code: q.code, required: q.evidence_requirements });
    }
  }

  for (const h of hazards) {
    const hz = kb.hazards[h.split("\u0000")[1]];
    if (hz && hz.specialist_module) addSpec(hz.specialist_module, `hazard:${h.split("\u0000")[1]}`);
  }
  for (const mod of Object.keys(specialists).sort()) {
    if (!MODULE_DOC.has(mod) && kb.documents[GENERIC_SPEC_DOC]) addDoc(GENERIC_SPEC_DOC, `specialist:${mod}`, mod);
  }
  const documents = Object.keys(reqdocs).sort().map((k) => {
    const d = reqdocs[k], req = kb.documents[d.code], ds = docsState[k] || {};
    let st = ds.status || "requested";
    const exp = parseDay(ds.expires_on);
    if (st === "received" && exp && exp < today) st = "expired";
    return { ...d, name: req.name, category: req.category, status: st, blocking_if_missing: req.blocking_if_missing,
      expiration_required: !!req.expiration_required, expires_on: exp ? dayIso(exp) : null,
      days_to_expiry: exp ? daysBetween(exp, today) : null, evidence_ids: ds.evidence_ids || [] };
  });
  return {
    task_codes: taskCodes,
    progress: { required, done, percent: required ? pyRound(100 * done / required) : 0 },
    next_questions: nextQuestions, open_items: openItems, missing_evidence: missingEvidence,
    documents, equipment_instances: equipmentInstances, active_keys: [...activeKeys], merged_duplicates: merged,
    expiring_days: EXPIRING_DAYS,
  };
}

// Dati del sopralluogo che l'auditor vede come "risposte date": stesso contenuto di `_answered` del server.
export function answeredList(kb, survey, ev) {
  const out = [];
  for (const k of ev.active_keys) {
    const a = (survey.answers || {})[k];
    if (!a) continue;
    const q = kb.questions[a.question_code];
    out.push({ key: k, task_code: a.task_code || null, equipment_code: a.equipment_code || null, question_code: q.code, text: q.text,
      help_text: q.help_text, answer_type: q.answer_type, options: q.options, evidence_requirements: q.evidence_requirements,
      critical: q.critical, state: a.state, value: a.value === undefined ? null : a.value, evidence_ids: a.evidence_ids || [] });
  }
  return out;
}
